import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCreditCard, FiPhone, FiSettings, FiDollarSign, FiCheck } = FiIcons;

const HowItWorks = () => {
  const steps = [
    {
      icon: FiCreditCard,
      title: "Pay Yearly for 3CX License",
      description: "Simple annual licensing based on concurrent calls, not per user. Get unlimited users with the capacity you need.",
      highlight: "Pay only for simultaneous calls"
    },
    {
      icon: FiPhone,
      title: "Choose Your Carrier",
      description: "Select from our trusted carrier partners based on your needs, location, and budget requirements. You always own your account with the carrier and control all billing and services.",
      highlight: "Multiple options for best rates"
    },
    {
      icon: FiSettings,
      title: "We Handle Everything",
      description: "Complete setup, configuration, training, and ongoing support. Zero technical hassle for your team.",
      highlight: "Full white-glove service"
    }
  ];

  const carriers = [
    { name: "Flowroute", logo: "🌊" },
    { name: "Skyetel", logo: "☁️" },
    { name: "Sip.us", logo: "🇺🇸" },
    { name: "SipTrunk", logo: "📞" },
    { name: "Twilio", logo: "🔗" },
    { name: "VOIP.ms", logo: "📡" }
  ];

  const benefits = [
    "Complete cost transparency - no hidden fees",
    "Flexibility to switch carriers if needed",
    "Competitive rates from multiple providers",
    "Local support for both license and carrier issues"
  ];

  return (
    <section id="how-it-works" className="py-20 bg-white">
      <div className="w-4/5 mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            How It Works
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Simple, transparent pricing with the flexibility to control your costs
          </p>
        </motion.div>

        {/* Steps */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              className="text-center relative"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              viewport={{ once: true }}
            >
              <div className="bg-primary-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                <SafeIcon icon={step.icon} className="h-10 w-10 text-primary-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                {step.title}
              </h3>
              <p className="text-gray-600 mb-4 leading-relaxed">
                {step.description}
              </p>
              <div className="bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-medium">
                {step.highlight}
              </div>
              {/* Arrow for desktop */}
              {index < steps.length - 1 && (
                <div className="hidden md:block absolute top-10 -right-4 text-gray-300">
                  <SafeIcon icon={FiIcons.FiArrowRight} className="h-6 w-6" />
                </div>
              )}
            </motion.div>
          ))}
        </div>

        {/* Carrier Partners */}
        <motion.div
          className="bg-gray-50 rounded-2xl p-8 mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h3 className="text-2xl font-bold text-gray-900 text-center mb-8">
            Choose From Our Trusted Carrier Partners
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {carriers.map((carrier, index) => (
              <motion.div
                key={index}
                className="bg-white p-4 rounded-lg text-center hover:shadow-md transition-shadow"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="text-2xl mb-2">{carrier.logo}</div>
                <div className="text-sm font-medium text-gray-700">{carrier.name}</div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Benefits */}
        <motion.div
          className="bg-primary-600 rounded-2xl p-8 text-white"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <div className="flex items-center justify-center mb-6">
            <SafeIcon icon={FiDollarSign} className="h-8 w-8 mr-3" />
            <h3 className="text-2xl font-bold">
              Why This Model Gives You More Control
            </h3>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                className="flex items-center space-x-3"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <SafeIcon icon={FiCheck} className="h-5 w-5 text-green-300 flex-shrink-0" />
                <span className="text-white/90">{benefit}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default HowItWorks;